import { useI18n } from '../../../../shared/i18n/useI18n.js'

import { calculateCompassAngle }    from '../../hooks/useModelFrames.js'

import { ButtonHero }             from '../ButtonHero/ButtonHero.jsx'
import { DirectionTicker }        from './DirectionTicker.jsx'
import { ModelOutlineControlsDock } from './ModelOutlineControlsDock.jsx'
import { ModelRotateControls }    from './ModelRotateControls.jsx'
import { OutlineEyeControl }      from './OutlineEyeControl.jsx'
import { OutlineLayerSelector }   from './OutlineLayerSelector.jsx'
import { resolveModelOutlineControlsState } from './modelOutlineControlsState.js'

import { ArrowLeft }     from '../../../../shared/ui/icons/ArrowLeft.jsx'
import { Close }         from '../../../../shared/ui/icons/Close.jsx'
import { CompassNorth }  from '../../../../shared/ui/icons/CompassNorth.jsx'
import { Drone }         from '../../../../shared/ui/icons/Drone.jsx'

import styles from './ModelControls.module.scss'
import { HeroCtaButton } from '../HeroButtonCta/HeroButtonCta.jsx'

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Overlay controls for the 360° ModelView.
 *
 * Props:
 *   compassElRef       {React.RefObject}  — attached to compass icon for DOM rotation
 *   onGoToNorth        {() => void}
 *   isOutlineVisible   {boolean}
 *   onToggleOutline    {() => void}
 *   availableOutlines  {string[]}       — HERO_OUTLINE[] for current scope
 *   activeOutline      {string}         — HERO_OUTLINE
 *   onOutlineChange    {(string) => void}
 *   activeView         {string}         — HERO_VIEW
 *   hasSiteModel       {boolean}        — show Back only when has site to return to
 *   hasVisualization   {boolean}        — show Close only when vis available
 *   onBack             {() => void}
 *   onClose            {() => void}
 *   onRotateLeft       {() => void}     — single-step (hook handles frame change)
 *   onRotateRight      {() => void}
 *   onZoomIn           {() => number}
 *   onZoomOut          {() => number}
 *   activeBuilding     {string|null}    — building id_cad for rotate label
 *   activeBuildingName {string|null}
 *   isSingleBuilding   {boolean}
 *   isMobile           {boolean}
 *   isLoading          {boolean}        — when true hides all controls except Back/Close
 *   loadingProgress    {number}
 *   colorMode          {string}
 *   onColorModeChange  {(string) => void}
 *   availableColorModes {Array<string|{value,label}>}
 *   displayableProducts {object[]}
 *   hoveredProduct     {object|null}
 *   colorModeDeciles   {Map<string,string>|null}
 *   activeFiltersConfig {object|null}
 *   defaultFiltersConfig {object|null}
 *   setActiveFiltersConfig {(object|function) => void}
 *   activeFiltersCount {number}
 *   showSoldHighlightNote {boolean}
 *   onShowFilters {() => void}
 *   onLegendHover      {(string|null) => void}
 *   currentFrameRef    {React.MutableRefObject<number>}
 *   framesCount        {number}
 *   azimuth            {number}
 */
export function ModelControls({
  compassElRef,
  onGoToNorth,
  isOutlineVisible,
  onToggleOutline,
  availableOutlines = [],
  activeOutline,
  activeProduct,
  onOutlineChange,
  activeView,
  hasSiteModel,
  hasVisualization,
  onBack,
  onClose,
  onRotateLeft,
  onRotateRight,
  onZoomIn,
  onZoomOut,
  activeBuilding,
  activeBuildingName,
  isSingleBuilding,
  isMobile,
  isLoading = false,
  loadingProgress = 0,
  colorMode,
  onColorModeChange,
  availableColorModes = [],
  displayableProducts = [],
  legendContainerRef,
  colorModeDeciles = null,
  activeFiltersConfig = null,
  defaultFiltersConfig = null,
  setActiveFiltersConfig,
  activeFiltersCount = 0,
  showSoldHighlightNote = false,
  onShowFilters,
  onLegendHover,
  currentFrameRef,
  framesCount,
  azimuth = 0,
  siteRenderGaussian,
}) {
  // ── Derived ─────────────────────────────────────────────────────────────────
const { t} = useI18n();

  const {
    showBack,
    showSelectorBackButton,
    useWideOutlineButtons,
  } = resolveModelOutlineControlsState({
    activeView,
    hasSiteModel,
    isOutlineVisible,
    availableOutlines,
    activeOutline,
    displayableProducts,
  })

  // Close: not showing Back AND has visualization
  const showClose = !showBack && hasVisualization

  // Compass node mounts only after loading; provide correct initial transform
  // so there is no visible snap from 0deg to computed angle.
  const initialCompassDeg = calculateCompassAngle(
    currentFrameRef?.current ?? 0,
    framesCount,
    azimuth,
  )

  const onGaussian = () => {
    window.open('https://demo.cadaplus.com/demo-premium-gaussian/', '_blank', 'noopener,noreferrer')
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className={styles.modelControlsHolder}>

      {/* ── Top bar ── compass / overlay toggle / back–close ─────────────────── */}
      <div className={styles.topBar}>

        {!isLoading && (
          <DirectionTicker
            currentFrameRef={currentFrameRef}
            framesCount={framesCount}
            azimuth={azimuth}
          />
        )}

        {/* Left group: compass + toggle overlay — hidden during loading */}
        {isLoading ? <span /> : (
          <div className={styles.buttonsGroup}>
            <ButtonHero className={styles.heroButton} onClick={onGoToNorth}>
              {/* <div style={{
                transform:`scaleY(0.85)`,
                display: 'flex',
                height: '100%',
                alignContent: 'center',
                justifyContent: 'center',
              }}> */}
              <CompassNorth
                ref={compassElRef}
                className={styles.topIcon}
                style={{ transform: `rotate(${initialCompassDeg}deg)` }}
              />
              {/* </div> */}
            </ButtonHero>

            <OutlineEyeControl
              isOutlineVisible={isOutlineVisible}
              activeOutline={activeOutline}
              onToggleOutline={onToggleOutline}
              colorMode={colorMode}
              onColorModeChange={onColorModeChange}
              availableColorModes={availableColorModes}
              isMobile={isMobile}
              buttonClassName={styles.heroButton}
              buttonActiveClassName={styles.heroButtonActive}
              iconClassName={styles.topIcon}
              activeFiltersConfig={activeFiltersConfig}
              activeFiltersCount={activeFiltersCount}
              activeProduct={activeProduct}
              colorModeDeciles={colorModeDeciles}
              defaultFiltersConfig={defaultFiltersConfig}
              legendContainerRef={legendContainerRef}
              onLegendHover={onLegendHover}
              onShowFilters={onShowFilters}
              products={displayableProducts}
              setActiveFiltersConfig={setActiveFiltersConfig}
              showSoldHighlightNote={showSoldHighlightNote}
            />
          </div>
        )}

        {/* Right group: back or close */}
        <div className={styles.buttonsGroup}>
          {siteRenderGaussian && (
            // <ButtonHero className={[
            //     styles.heroButton,
            //     styles.ctaButton
            //   ].join(' ')} 
            //   onClick={onGaussian}
            // >
            //   {!isMobile && ( 
            //     <span>Eksploruj przelot dronem</span>
            //   )}
            //   {isMobile && (<Drone className={styles.topIcon} />)}
            // </ButtonHero>
                      <HeroCtaButton 
                        text={t('hero.open_drone_flyover')}
                        Icon={({ className, isExpanded }) => (
                            <Drone 
                                className={[
                                  className,
                                  (isExpanded || isMobile) ? styles.droneFly : styles.droneFlyIdle].join(' ')
                                } 
                            />
                        )}
                        onClick={onGaussian}
                        disableExpand={isMobile} 
                        autoExpand={!isMobile}
                        className={isMobile ? styles.smallerMobile: ''}
                      />
          )}
          {showBack && (
            <ButtonHero className={styles.heroButton} onClick={onBack}>
              <ArrowLeft className={styles.topIcon} />
            </ButtonHero>
          )}
          {showClose && (
            <ButtonHero className={styles.heroButton} onClick={onClose}>
              <Close className={styles.topIcon} />
            </ButtonHero>
          )}
        </div>

      </div>


      {/* ── Bottom bar ── rotate controls / outline layer selector ─────────────────── */}
      {!isMobile && (
        <div className={styles.bottomBar}>
          <span style={{ display: 'block', width: '14em' }} />

          <ModelRotateControls
            activeView={activeView}
            activeBuilding={activeBuilding}
            activeBuildingName={activeBuildingName}
            isSingleBuilding={isSingleBuilding}
            isLoading={isLoading}
            loadingProgress={loadingProgress}
            onRotateLeft={onRotateLeft}
            onRotateRight={onRotateRight}
            onZoomIn={onZoomIn}
            onZoomOut={onZoomOut}
          />

          <ModelOutlineControlsDock
            isLoading={isLoading}
            activeView={activeView}
            hasSiteModel={hasSiteModel}
            isOutlineVisible={isOutlineVisible}
            availableOutlines={availableOutlines}
            activeOutline={activeOutline}
            onOutlineChange={onOutlineChange}
            onBack={onBack}
            colorMode={colorMode}
            onColorModeChange={onColorModeChange}
            availableColorModes={availableColorModes}
            displayableProducts={displayableProducts}
            legendContainerRef={legendContainerRef}
            colorModeDeciles={colorModeDeciles}
            activeFiltersConfig={activeFiltersConfig}
            defaultFiltersConfig={defaultFiltersConfig}
            setActiveFiltersConfig={setActiveFiltersConfig}
            activeFiltersCount={activeFiltersCount}
            showSoldHighlightNote={showSoldHighlightNote}
            onShowFilters={onShowFilters}
            onLegendHover={onLegendHover}
          />
        </div>
      )}

      {/* ── Mobile bottom bar ────────────────────────────────────────────────── */}
      {isMobile && (
        <div className={styles.mobileBottomBar}>
          {/* <span style={{ display: 'block', width: '14em' }} /> */}

          {!isLoading && (
            <OutlineLayerSelector
              isMobile
              activeOutline={activeOutline}
              availableOutlines={availableOutlines}
              onOutlineChange={onOutlineChange}
              showBackButton={showSelectorBackButton}
              onBack={onBack}
              useWideOutlineButtons={useWideOutlineButtons}
            />
          )}
          <ModelRotateControls
            isMobile
            activeView={activeView}
            activeBuilding={activeBuilding}
            activeBuildingName={activeBuildingName}
            isSingleBuilding={isSingleBuilding}
            isLoading={isLoading}
            loadingProgress={loadingProgress}
            onRotateLeft={onRotateLeft}
            onRotateRight={onRotateRight}
            onZoomIn={onZoomIn}
            onZoomOut={onZoomOut}
          />


        </div>
      )}

    </div>
  )
}
